import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { getNextAccessiblePathInJourneyStep, useNavigationRBAC } from '../utils/navigation-rbac'
import { useUser } from '../contexts/UserContext'
import { UserType } from '../types/user'
import { breakpoints } from '../utils/constants'

export enum NavigationActionType {
  TOGGLE_NAVIGATION = 'TOGGLE_NAVIGATION',
  SET_NAVIGATION_STATE = 'SET_NAVIGATION_STATE',
  SET_JOURNEY_STEP = 'SET_JOURNEY_STEP',
  SET_IS_TABLET = 'SET_IS_TABLET',
  SET_OPEN_HOVER_CARD_ID = 'SET_OPEN_HOVER_CARD_ID'
}

export enum JourneyStep {
  CHECKOUT = 'CHECKOUT',
  CART = 'CART',
  PDP = 'PDP',
  SHOP_PASS = 'SHOP_PASS',
  NONE = 'NONE'
}

type NavigationAction =
  | { type: NavigationActionType.TOGGLE_NAVIGATION }
  | {
      type: NavigationActionType.SET_NAVIGATION_STATE
      payload: { sidebarOpen: boolean; secondarySidebarOpen: boolean }
    }
  | { type: NavigationActionType.SET_JOURNEY_STEP; payload: JourneyStep }
  | { type: NavigationActionType.SET_IS_TABLET; payload: boolean }
  | { type: NavigationActionType.SET_OPEN_HOVER_CARD_ID; payload: string | null }

interface NavigationState {
  sidebarOpen: boolean
  secondarySidebarOpen: boolean
  journeyStep: JourneyStep
  isTablet: boolean
  openHoverCardId: string | null
}

const initialState: NavigationState = {
  sidebarOpen: true,
  secondarySidebarOpen: false,
  journeyStep: JourneyStep.NONE,
  isTablet: false,
  openHoverCardId: null
}

function navigationReducer(state: NavigationState, action: NavigationAction): NavigationState {
  switch (action.type) {
    case NavigationActionType.TOGGLE_NAVIGATION:
      if (state.sidebarOpen && !state.secondarySidebarOpen) {
        return { ...state, sidebarOpen: false, secondarySidebarOpen: true }
      } else if (!state.sidebarOpen && state.secondarySidebarOpen) {
        return { ...state, sidebarOpen: true, secondarySidebarOpen: false }
      } else {
        return { ...state, sidebarOpen: true, secondarySidebarOpen: false }
      }
    case NavigationActionType.SET_NAVIGATION_STATE:
      return { ...state, ...action.payload }
    case NavigationActionType.SET_JOURNEY_STEP:
      return { ...state, journeyStep: action.payload }
    case NavigationActionType.SET_IS_TABLET:
      if (!action.payload) {
        if (state.journeyStep === JourneyStep.NONE) {
          return { ...state, isTablet: action.payload, sidebarOpen: true, secondarySidebarOpen: false }
        } else {
          return { ...state, isTablet: action.payload, sidebarOpen: false, secondarySidebarOpen: true }
        }
      }

      return { ...state, isTablet: action.payload, sidebarOpen: false, secondarySidebarOpen: false }
    case NavigationActionType.SET_OPEN_HOVER_CARD_ID:
      return { ...state, openHoverCardId: action.payload }

    default:
      return state
  }
}

interface NavigationContextType {
  state: NavigationState
  actions: {
    toggleNavigation: () => void
    setNavigationState: (sidebarOpen: boolean, secondarySidebarOpen: boolean) => void
    setJourneyStep: (step: JourneyStep) => void
    setJourneyStepAndNavigate: (step: JourneyStep, user: UserType | undefined) => void
    navigateWithSubmenuHandling: (path: string) => void
    toggleAndRedirect: (path: string) => void
    resetAndRedirect: (path: string) => void
    handleSetJourneyStep: (step: JourneyStep) => void
    setOpenHoverCardId: (id: string | null) => void
  }
  getters: {
    sidebarOpen: boolean
    secondarySidebarOpen: boolean
    journeyStep: JourneyStep
    isTablet: boolean
    openHoverCardId: string | null
  }
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined)

const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(navigationReducer, initialState)
  const router = useRouter()
  const { user } = useUser()
  const currentIsTabletRef = useRef(false)
  const isOnboarding = router.pathname.startsWith('/onboarding')

  const { getNextAccessiblePath } = useNavigationRBAC()

  useEffect(() => {
    const checkTabletSize = () => {
      const isTablet = window.innerWidth <= (isOnboarding ? breakpoints.TABLET_ONBOARDING : breakpoints.TABLET_DEFAULT)

      if (currentIsTabletRef.current !== isTablet) {
        currentIsTabletRef.current = isTablet
        dispatch({ type: NavigationActionType.SET_IS_TABLET, payload: isTablet })
      }
    }

    checkTabletSize()

    window.addEventListener('resize', checkTabletSize)

    return () => {
      window.removeEventListener('resize', checkTabletSize)
    }
  }, [])

  const handleToggleNavigation = useCallback(() => {
    dispatch({ type: NavigationActionType.TOGGLE_NAVIGATION })
  }, [dispatch])

  const handleSetNavigationState = useCallback(
    (sidebarOpen: boolean, secondarySidebarOpen: boolean) => {
      dispatch({ type: NavigationActionType.SET_NAVIGATION_STATE, payload: { sidebarOpen, secondarySidebarOpen } })
    },
    [dispatch]
  )

  const handleSetJourneyStep = useCallback(
    (step: JourneyStep) => {
      dispatch({ type: NavigationActionType.SET_OPEN_HOVER_CARD_ID, payload: null })
      dispatch({ type: NavigationActionType.SET_JOURNEY_STEP, payload: step })
      if (step === JourneyStep.NONE) {
        if (!state.isTablet) {
          handleSetNavigationState(true, false)
        } else {
          handleSetNavigationState(false, false)
        }
      } else {
        if (!state.isTablet) {
          handleSetNavigationState(false, true)
        }
      }
    },
    [dispatch, handleSetNavigationState, state.isTablet]
  )

  const handleSetJourneyStepAndNavigate = useCallback(
    (step: JourneyStep, user: UserType | undefined) => {
      dispatch({ type: NavigationActionType.SET_OPEN_HOVER_CARD_ID, payload: null })
      handleSetJourneyStep(step)
      const accessibleRoute = getNextAccessiblePath(step, user)
      router.push(accessibleRoute)
    },
    [handleSetJourneyStep, router, getNextAccessiblePath]
  )

  const navigateWithSubmenuHandling = useCallback(
    (path: string) => {
      handleSetJourneyStep(JourneyStep.NONE)
      if (!state.sidebarOpen && !state.isTablet) {
        handleSetNavigationState(true, false)
      }
      if (state.isTablet) {
        handleSetNavigationState(false, false)
      }
      router.push(path)
    },
    [state.sidebarOpen, state.isTablet, handleSetJourneyStep, handleSetNavigationState, router]
  )

  const toggleAndRedirect = useCallback(
    (path: string) => {
      if (!state.isTablet) {
        handleToggleNavigation()
      }
      handleSetJourneyStep(JourneyStep.NONE)

      router.push(path)
    },

    [handleToggleNavigation, handleSetJourneyStep, router, state.isTablet]
  )

  const resetAndRedirect = useCallback(
    (path: string) => {
      handleSetJourneyStep(JourneyStep.NONE)

      router.push(path)
    },

    [handleSetJourneyStep, router]
  )

  const actions = {
    toggleNavigation: handleToggleNavigation,
    setNavigationState: handleSetNavigationState,
    setJourneyStep: handleSetJourneyStep,
    setJourneyStepAndNavigate: handleSetJourneyStepAndNavigate,
    navigateWithSubmenuHandling: navigateWithSubmenuHandling,
    toggleAndRedirect: toggleAndRedirect,
    resetAndRedirect: resetAndRedirect,
    handleSetJourneyStep: handleSetJourneyStep,
    setOpenHoverCardId: (id: string | null) =>
      dispatch({ type: NavigationActionType.SET_OPEN_HOVER_CARD_ID, payload: id })
  }

  const getters = {
    sidebarOpen: state.sidebarOpen,
    secondarySidebarOpen: state.secondarySidebarOpen,
    journeyStep: state.journeyStep,
    isTablet: state.isTablet,
    openHoverCardId: state.openHoverCardId
  }

  return <NavigationContext.Provider value={{ state, actions, getters }}>{children}</NavigationContext.Provider>
}

const useNavigation = () => {
  const context = useContext(NavigationContext)
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider')
  }
  return context
}

export { NavigationProvider }
export default useNavigation
